import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { useCallback, useState } from 'react';
import { buildReportModalPdfPayload } from '@/domain/reporting/reporting-modal-summary';
import {
    AibackReportAspectRowEntity,
    AibackReportPdfTopItemEntity,
    AibackReportPdfTreemapItemEntity,
    AibackReportRatingTimelinePointEntity,
    AibackReportResponseTimeTimelinePointEntity,
} from '@/entities/aiback-report.entity';
import { useMarketplaceApi } from '@/context/marketplace.context';

type ReportModalRatingBucket = {
    stars: number;
    value: number;
};

const productsReportExportLogger = createClientLogger({
    scope: 'products-report-export',
});

type UseProductsReportModalExportPdfParams = {
    accessToken: string;
    companyName: string;
    periodFrom: string;
    periodTo: string;
    selectedPlatformLabels: string[];
    selectedProductLabels: string[];
    selectedBrandLabels: string[];
    totalReviews: number;
    totalAnswers: number;
    answersShare: number | null;
    averageResponseTimeMinutes: number | null;
    answeredReviewsCount: number;
    totalAiAnalyses: number;
    companyAverageRating: number | null;
    channelsCount: number;
    productsCount: number;
    brandsCount: number;
    topChannels: AibackReportPdfTopItemEntity[];
    topProducts: AibackReportPdfTopItemEntity[];
    topBrands: AibackReportPdfTopItemEntity[];
    ratingBuckets: ReportModalRatingBucket[];
    averageRatingTimeline: AibackReportRatingTimelinePointEntity[];
    averageResponseTimeTimeline: AibackReportResponseTimeTimelinePointEntity[];
    treemapItems: AibackReportPdfTreemapItemEntity[];
    aspectRows: AibackReportAspectRowEntity[];
    positiveHighlights: string[];
    positiveHighlightsHiddenCount?: number;
    negativeHighlights: string[];
    negativeHighlightsHiddenCount?: number;
    neutralHighlights?: string[];
    neutralHighlightsHiddenCount?: number;
};

export const useProductsReportModalExportPdf = ({
    accessToken,
    companyName,
    periodFrom,
    periodTo,
    selectedPlatformLabels,
    selectedProductLabels,
    selectedBrandLabels,
    totalReviews,
    totalAnswers,
    answersShare,
    averageResponseTimeMinutes,
    answeredReviewsCount,
    totalAiAnalyses,
    companyAverageRating,
    channelsCount,
    productsCount,
    brandsCount,
    topChannels,
    topProducts,
    topBrands,
    ratingBuckets,
    averageRatingTimeline,
    averageResponseTimeTimeline,
    treemapItems,
    aspectRows,
    positiveHighlights,
    positiveHighlightsHiddenCount,
    negativeHighlights,
    negativeHighlightsHiddenCount,
    neutralHighlights = [],
    neutralHighlightsHiddenCount,
}: UseProductsReportModalExportPdfParams): {
    isExportingPdf: boolean;
    exportPdf: () => Promise<void>;
} => {
    const { reviewsApi } = useMarketplaceApi();
    const [isExportingPdf, setIsExportingPdf] = useState(false);

    const exportPdf = useCallback(async () => {
        if (isExportingPdf) {
            return;
        }

        const payload = buildReportModalPdfPayload({
            companyName,
            periodFrom,
            periodTo,
            generatedAt: new Date().toISOString(),
            selectedPlatformLabels,
            selectedProductLabels,
            selectedBrandLabels,
            totalReviews,
            totalAnswers,
            answersShare,
            averageResponseTimeMinutes,
            answeredReviewsCount,
            totalAiAnalyses,
            companyAverageRating,
            channelsCount,
            productsCount,
            brandsCount,
            topChannels,
            topProducts,
            topBrands,
            ratingBuckets,
            averageRatingTimeline,
            averageResponseTimeTimeline,
            treemapItems,
            aspectRows,
            positiveHighlights,
            positiveHighlightsHiddenCount,
            negativeHighlights,
            negativeHighlightsHiddenCount,
            neutralHighlights,
            neutralHighlightsHiddenCount,
        });

        setIsExportingPdf(true);

        try {
            await reviewsApi.downloadAibackReportPdf(payload, { authToken: accessToken });
        } catch (error) {
            logClientError({
                logger: productsReportExportLogger,
                message: '[client.products-report.pdf-export] Failed to export products report PDF.',
                error,
            });
        } finally {
            setIsExportingPdf(false);
        }
    }, [
        accessToken,
        answersShare,
        answeredReviewsCount,
        aspectRows,
        averageRatingTimeline,
        averageResponseTimeMinutes,
        brandsCount,
        channelsCount,
        companyAverageRating,
        companyName,
        isExportingPdf,
        periodFrom,
        periodTo,
        positiveHighlights,
        positiveHighlightsHiddenCount,
        selectedBrandLabels,
        selectedPlatformLabels,
        selectedProductLabels,
        productsCount,
        ratingBuckets,
        averageResponseTimeTimeline,
        negativeHighlights,
        negativeHighlightsHiddenCount,
        neutralHighlights,
        neutralHighlightsHiddenCount,
        topChannels,
        topBrands,
        topProducts,
        totalAiAnalyses,
        totalAnswers,
        totalReviews,
        treemapItems,
    ]);

    return {
        isExportingPdf,
        exportPdf,
    };
};
